import { useState } from 'react';
import { TextInput, Textarea } from '@mantine/core';
import { IconTrash, IconPencil, IconCheck, IconX } from '@tabler/icons-react';
import { FlexibleDateInput } from './FlexibleDateInput';
import type { ActiveConditionLocal } from '../../types/medicalHistory';

interface ActiveConditionFormProps {
  condition: ActiveConditionLocal;
  onChange: (updated: ActiveConditionLocal) => void;
  onRemove: () => void;
  onSubmit: () => void;
  disabled?: boolean;
  isNew?: boolean;
}

// Format date for display
function formatDate(date: ActiveConditionLocal['onsetDate']): string {
  if (!date) return '';
  const { value, precision } = date;
  if (precision === 'year') return value;
  if (precision === 'month') {
    const [year, month] = value.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month, 10) - 1]} ${year}`;
  }
  // Full date
  const d = new Date(value);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function ActiveConditionForm({
  condition,
  onChange,
  onRemove,
  onSubmit,
  disabled = false,
  isNew = false,
}: ActiveConditionFormProps) {
  const [isEditing, setIsEditing] = useState(isNew);

  const handleChange = (
    field: keyof ActiveConditionLocal,
    value: string | ActiveConditionLocal['onsetDate']
  ) => {
    onChange({ ...condition, [field]: value });
  };

  const handleSave = () => {
    if (condition.conditionName.trim()) {
      onSubmit();
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    if (isNew) {
      onRemove();
    } else {
      setIsEditing(false);
    }
  };

  // View mode - display as card
  if (!isEditing) {
    return (
      <div className="card p-4 group">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="font-medium text-gray-200">{condition.conditionName}</h4>
            <div className="mt-1 text-sm text-gray-400 space-y-0.5">
              {condition.onsetDate && (
                <p>Onset: {formatDate(condition.onsetDate)}</p>
              )}
              {condition.comments && (
                <p className="text-gray-500 italic">{condition.comments}</p>
              )}
            </div>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors disabled:opacity-50"
              title="Edit"
            >
              <IconPencil size={16} />
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
              title="Delete"
            >
              <IconTrash size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode - show form
  return (
    <div className="card p-4 border-cyan-500/30">
      <div className="space-y-4">
        {/* Condition Name */}
        <TextInput
          label="Condition Name"
          placeholder="e.g., High Blood Pressure, Type 2 Diabetes"
          value={condition.conditionName}
          onChange={(e) => handleChange('conditionName', e.target.value)}
          disabled={disabled}
          required
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        {/* Date of Onset */}
        <FlexibleDateInput
          label="Date of Onset"
          value={condition.onsetDate}
          onChange={(date) => handleChange('onsetDate', date)}
          disabled={disabled}
        />

        {/* Comments */}
        <Textarea
          label="Comments"
          placeholder="Share anything helpful — e.g., which doctor manages this, current treatment approach, how it affects your daily life"
          value={condition.comments || ''}
          onChange={(e) => handleChange('comments', e.target.value)}
          disabled={disabled}
          minRows={2}
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={disabled}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            <IconX size={14} />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled || !condition.conditionName.trim()}
            className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <IconCheck size={14} />
            {isNew ? 'Add Condition' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
